import { Button } from '@civilgrid/ui/components';
import { Link, useNavigate } from '@tanstack/react-router';
import axios from 'axios';
import { Controller, useForm } from 'react-hook-form';
import { register as apiRegister, getAuthCode } from '~/api/auth2';
import { useQuery } from '~/common/routerUtils';
import { emailRegex } from '~/components2/forms/common';
import Form from '~/components2/forms/Form';
import Input from '~/components2/forms/Input';
import { Notify } from '~/components2/Notify';
import PageLayout from './PageLayout';
import RegisterContact from './RegisterContact';
import store from './store';

interface RegisterForm {
  firstName: string;
  lastName: string;
  email: string;
  companyName: string;
  phone: string;
}

export default function RegisterComponent() {
  const query = useQuery();
  const navigate = useNavigate();

  const { handleSubmit, control } = useForm<RegisterForm>({
    defaultValues: {
      email: query?.get('email') ?? store.email ?? '',
      firstName: query.get('firstName') || '',
      lastName: query.get('lastName') || '',
      companyName: query.get('companyName') || '',
      phone: query.get('phone') || '',
    },
  });

  const onFormSubmit = async ({ firstName, lastName, email, companyName, phone }: RegisterForm) => {
    try {
      const response = await apiRegister({
        firstName,
        lastName,
        email,
        companyName,
        phone,
      });

      // Handle invited user case - redirect to login with authcode
      if (response.data?.isInvitedUser && response.data?.redirect) {
        store.email = email;
        navigate({ to: response.data.redirect });

        Notify.info({
          title: 'Welcome!',
          message: 'You were already invited to CivilGrid. Please login using the auth code sent to your email.',
          duration: 5_000,
        });
        return;
      }

      Notify.info({
        title: `Registration Successful`,
        message: (
          <div className="space-y-2">
            <div>
              To ensure you have the best experience with our product, we offer a personalized onboarding process for
              all new users. A member of our team will reach out shortly to schedule a 1:1 account setup and training
              session.
            </div>
            <div>
              If you’d prefer, you can book a convenient time directly with our onboarding team using the calendar
              below.
            </div>
            <div>We look forward to helping you get started!</div>
          </div>
        ),

        duration: 30_000,
      });
    } catch (e) {
      console.error(e);

      if (!axios.isAxiosError(e) || !e.response) {
        Notify.error({
          title: 'Error',
          message: 'An unexpected error occurred. Please try again.',
          duration: 5_000,
        });
        return;
      }

      // 409 indicates the user already has an account
      // 410 indicates the user has an allowed domain
      // in both cases, we want to send them to through the auth code flow

      switch (e.response.status) {
        case 409:
          store.email = email;
          await getAuthCode(email);
          navigate({ to: '/login/authCode' });

          Notify.info({
            title: 'Existing account',
            message: `${email} already has an account. Please login using the auth code.`,
            duration: 5_000,
          });
          break;

        case 410: {
          store.email = email;
          const data = await getAuthCode(email);

          if (data.redirect) {
            if (data.redirect.startsWith('http')) {
              window.location.href = data.redirect;
            } else {
              navigate({ to: data.redirect });
            }
          } else {
            navigate({ to: '/login/authCode' });
          }

          Notify.info({
            title: 'Your colleagues are already using CivilGrid!',
            message:
              'Your colleagues are already using CivilGrid! Please login using the auth code sent to your email.',
            duration: 5_000,
          });
          break;
        }

        case 400:
          Notify.info({
            title: 'Invalid Email Domain',
            message: 'Please register using your work email.',
            duration: 5_000,
          });

          break;

        default:
          Notify.info({
            title: 'Error',
            message: 'There was an error registering your account. Please try again or contact Support.',
            duration: 5_000,
          });
      }
    }
  };

  return (
    <PageLayout data-testid="register-page" rightSideContent={<RegisterContact />}>
      <p className="py-6 text-sm">
        To access CivilGrid, you may sign up below, or book a demo for yourself on the Calendar on the right. You may
        also call{' '}
        <a href="tel:+14159421303" rel="noreferrer noopener" className="text-blue-50 underline hover:text-blue-60">
          415-942-1303
        </a>{' '}
        or email{' '}
        <a
          href="mailto:sales@civilgrid.com"
          rel="noreferrer noopener"
          className="text-blue-50 underline hover:text-blue-60"
        >
          sales@civilgrid.com
        </a>
        .
      </p>
      <Form className="space-y-2" onSubmit={handleSubmit(onFormSubmit)}>
        <Controller
          name="firstName"
          control={control}
          rules={{ required: 'Required' }}
          render={({ field, fieldState: { error } }) => (
            <Input
              label="First Name"
              placeholder="Alice"
              {...field}
              error={error?.message}
              data-testid="register-first-name-input"
            />
          )}
        />
        <Controller
          name="lastName"
          control={control}
          rules={{ required: 'Required' }}
          render={({ field, fieldState: { error } }) => (
            <Input
              label="Last Name"
              placeholder="Appleseed"
              {...field}
              error={error?.message}
              data-testid="register-last-name-input"
            />
          )}
        />
        <Controller
          name="email"
          control={control}
          rules={{
            required: 'Please enter email',
            pattern: {
              value: emailRegex,
              message: 'Invalid email',
            },
          }}
          render={({ field, fieldState: { error } }) => (
            <Input
              label="Email"
              placeholder="alice@acme-engineers.com"
              {...field}
              error={error?.message}
              data-testid="register-email-input"
            />
          )}
        />
        <Controller
          name="companyName"
          control={control}
          rules={{ required: 'Required' }}
          render={({ field, fieldState: { error } }) => (
            <Input
              label="Company Name"
              placeholder="ACME Engineers"
              {...field}
              error={error?.message}
              data-testid="register-company-name-input"
            />
          )}
        />
        <Controller
          name="phone"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Input
              label="Phone Number"
              placeholder="(xxx)-xxx-xxxx"
              {...field}
              error={error?.message}
              data-testid="register-phone-input"
            />
          )}
        />

        <Button type="submit" data-testid="register-submit-button">
          Register
        </Button>
      </Form>
      <div className="mt-2 text-xs">
        Already have an account?{' '}
        <Link className="text-blue-50 underline hover:text-blue-60" to="/login" data-testid="register-login-link">
          Login
        </Link>
      </div>
    </PageLayout>
  );
}
